import { useState } from 'react';
import { AxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { UpdateWholeFundInfoDtoOut } from '@/models/out/UpdateWholeFundInfoDtoOut';
import { FundStatus } from '@/models/common/FundStatus';

const extractServerError = (error: Error): string | null => {
  const data = (error as AxiosError).response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data) return String((data as { message: unknown }).message);
  return null;
};

/** Shared mutation for the per-section edit dialogs of the fund view page. */
export const useUpdateFund = (fundId: string, onSaved: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = usePutAuthApi<void, object, UpdateWholeFundInfoDtoOut>(
    `funds/${fundId}/management/update`,
    {
      onSuccess: () => {
        setServerError(null);
        queryClient.invalidateQueries({ queryKey: [`funds/${fundId}/management`] });
        queryClient.invalidateQueries({ queryKey: [`funds/management?status=${FundStatus.Active}`] });
        queryClient.invalidateQueries({ queryKey: [`funds/management?status=${FundStatus.Closed}`] });
        queryClient.invalidateQueries({ queryKey: [`funds/management?status=${FundStatus.Frozen}`] });
        onSaved();
      },
      onError: (error) => setServerError(extractServerError(error) ?? t('fundsManagement.saveError')),
    }
  );

  return {
    save: (dto: UpdateWholeFundInfoDtoOut) => mutation.mutate({ params: {}, body: dto }),
    isSaving: mutation.isPending,
    serverError,
    clearServerError: () => setServerError(null),
  };
};

export default useUpdateFund;
